import { useCallback, useMemo, useRef, useState } from 'react';
import { FormHeader, FormItem } from '@/interfaces/api/models';
import { buildAutosaveComposite, FormFieldValue, getBlankFormItemFromOtherItem, parseAutosaveComposite } from './form-helpers';
import { computeDraftKey, readDraft, writeDraft, deleteDraft, FormDraftSnapshot } from './form-autosave-storage';

const AUTOSAVE_DEBOUNCE_MS = 500;

export interface RestoredDraft {
    /** Keyed by the CURRENT session's real `FormItem.ID`s, ready to hand to `form.setValues`. */
    values: Record<string, FormFieldValue>;
    /**
     * Repeatable-section instances that existed when the draft was written but are not in the
     * current `formItemsMeta` (the user added them, then crashed before saving). The caller must
     * append these to `formItemsMeta` for their restored values to have anything to render into.
     */
    addedItems: FormItem[];
}

export function useFormAutosave({ formHeader, isNew, formItemsMeta }: { formHeader: FormHeader; isNew: boolean; formItemsMeta: FormItem[] }) {
    const draftKey = useMemo(() => computeDraftKey(formHeader, isNew), [formHeader, isNew]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [pendingRestore] = useState<FormDraftSnapshot | null>(() => {
        const existing = readDraft(draftKey);
        return existing && existing.sessionOpen ? existing : null;
    });

    /**
     * `values` MUST already be keyed by `buildAutosaveComposite` (see `FormDraftSnapshot.values`);
     * the caller does that re-keying because it is the one that holds both the form store and
     * `formItemsMeta`.
     */
    const scheduleAutosave = useCallback((values: Record<string, FormFieldValue>) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            writeDraft(draftKey, {
                formDefinitionID: formHeader.FormDefinitionID ?? '',
                formHeaderID: formHeader.ID ?? '',
                values,
                savedAt: new Date().toISOString(),
                sessionOpen: true,
            });
        }, AUTOSAVE_DEBOUNCE_MS);
    }, [draftKey, formHeader.FormDefinitionID, formHeader.ID]);

    /**
     * Maps a snapshot's composite-keyed values back onto *this* session's item IDs.
     *
     * The item IDs the snapshot was written under are gone (a never-saved form regenerates them on
     * every mount), so a key-for-key copy would write under IDs no `FormFieldBinding` reads and the
     * form would render blank despite the data being present — the bug this indirection exists to
     * prevent.
     */
    const restoreValues = useCallback((snapshot: FormDraftSnapshot): RestoredDraft => {
        const byComposite = new Map<string, FormItem>();
        formItemsMeta.forEach((item) => {
            const composite = buildAutosaveComposite(item);
            // First wins: the composite is unique per item set, so a collision would mean malformed
            // metadata — preferring the earlier item keeps the mapping deterministic either way.
            if (!byComposite.has(composite)) byComposite.set(composite, item);
        });

        const values: Record<string, FormFieldValue> = {};
        const addedItems: FormItem[] = [];

        const applyValue = (item: FormItem, value: FormFieldValue) => {
            const isDate = item.DataType === 'Date' || item.DataType === 'DateTime';
            values[item.ID] = isDate && typeof value === 'string' ? new Date(value) : value;
        };

        for (const [composite, value] of Object.entries(snapshot.values)) {
            const existing = byComposite.get(composite);
            if (existing) {
                applyValue(existing, value);
                continue;
            }

            // No current item carries this composite: it belongs to a repeatable-section instance
            // the user added before the crash. Recreate it from any sibling item of the same field
            // (which carries the section metadata `buildSectionTree` needs), exactly as
            // `onAddInstance` would have.
            const parsed = parseAutosaveComposite(composite);
            if (!parsed) continue;
            const template = formItemsMeta.find((item) => item.FormDefinitionFieldID === parsed.formDefinitionFieldID);
            // No template means the field no longer exists in the definition — nothing sensible to
            // restore it into, so the value is dropped rather than guessed at.
            if (!template) continue;

            const recreated = getBlankFormItemFromOtherItem(template, parsed.sectionGroup, parsed.parentSectionGroup);
            // `getBlankFormItemFromOtherItem` only honours the requested SectionGroup for a section
            // it believes is repeatable, so re-derive the composite instead of assuming it matched.
            // A mismatch means the snapshot describes an instance this definition cannot hold any
            // more; dropping the value beats attaching it to the wrong (or a duplicate) item.
            if (buildAutosaveComposite(recreated) !== composite) continue;
            byComposite.set(composite, recreated);
            addedItems.push(recreated);
            applyValue(recreated, value);
        }

        return { values, addedItems };
    }, [formItemsMeta]);

    const discardDraft = useCallback(() => deleteDraft(draftKey), [draftKey]);
    const clearOnCleanExit = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        deleteDraft(draftKey);
    }, [draftKey]);

    return { pendingRestore, scheduleAutosave, restoreValues, discardDraft, clearOnCleanExit };
}
